import React, { ReactNode, useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import { Button } from '@/components/atoms'
import { PosCalculateSuccessModal } from '@/components/organisms/accommodation-management/pos-calculate-success-modal'
import { fetchSmaregiTerminals, postSmaregiTransactionsLayaways } from '@/apis/aipass'
import { sameTerminalKey, TerminalType } from '@/models/smaregi/terminal'
import { TransactionType } from '@/models/smaregi/transaction'
import { initializeSelectBoxStyle } from '@/constants/form'
import { PosTerminal } from '@/models/local-storage'
import { AccountContext } from '@/contexts/account'
import { AdjustmentSalesSelectList } from '@/components/molecules/customer/sales-manager/adjustment-sales-select-list'
import { LoaderContextCreator } from '@/contexts/loader'
import { isStaffGuardCancelledError } from '@/libs/errors'

type PosCalculateModalProps = {
  reservationId?: string
  checkinId?: string
  transaction?: TransactionType
  onClose: (isUpdated: boolean) => void
  filterGuestRoomAssignId?: string[]
}

export const PosCalculateModal: React.FC<PosCalculateModalProps> = props => {
  const { t } = useTranslation()
  const { account } = useContext<any>(AccountContext)
  const { setIsLoading } = useContext(LoaderContextCreator())

  const [terminalOptions, setTerminalOptions] = useState<Array<{ value: number; label: ReactNode; data: TerminalType }>>([])
  const [selectTerminal, setSelectTerminal] = useState<TerminalType>()
  const [isShowSuccessModal, setShowSuccessModal] = useState<boolean>(false)
  const [selectedSaleIds, setSelectedSaleIds] = useState<string[]>([])
  const [sumSelectSalePrice, setSumSelectSalePrice] = useState<number>(0)
  const [isSending, setIsSending] = useState<boolean>(false)
  // 全画面ローダーはマウス操作しか遮らず、フォーカスが残った送信ボタンへの Enter のキーリピートは届く。
  // 同じ売上を同時に送るとスマレジに取置きが重複して作られるため、再描画を待たずに ref で弾く
  const isSendingRef = useRef(false)
  const isDisabledSend = !sumSelectSalePrice || terminalOptions.length === 0 || isSending

  const sendPosCalculate = () => {
    if (!selectTerminal || isSendingRef.current) {
      return
    }

    isSendingRef.current = true
    setIsSending(true)
    setIsLoading(true)
    localStorage.setItem(PosTerminal(account.hotel.id), sameTerminalKey(selectTerminal))
    postSmaregiTransactionsLayaways({
      contractId: selectTerminal.contractId,
      storeId: selectTerminal.storeId,
      terminalId: selectTerminal.terminalId,
      saleIds: selectedSaleIds,
    })
      .then(() => {
        setShowSuccessModal(true)
      })
      .catch(error => {
        if (!isStaffGuardCancelledError(error)) {
          alert('Server error')
        }
      })
      .finally(() => {
        isSendingRef.current = false
        setIsSending(false)
        setIsLoading(false)
      })
  }

  const changedTerminal = (terminalIndex: number) => {
    setSelectTerminal(terminalOptions[terminalIndex]?.data)
  }

  const fetchSmaregiTerminal = () => {
    fetchSmaregiTerminals().then((terminals: TerminalType[]) => {
      if (terminals.length === 0) {
        alert(t('POS terminal is not registered.'))
        return
      }
      setTerminalOptions(
        terminals.map((terminal, index) => ({
          value: index,
          label: terminal.terminalName,
          data: terminal,
        })),
      )
      const prevSelectedTerminalKey = localStorage.getItem(PosTerminal(account.hotel.id))
      if (prevSelectedTerminalKey) {
        const prevSelectedTerminalIndex = terminals.findIndex(terminal => sameTerminalKey(terminal) === prevSelectedTerminalKey)
        if (prevSelectedTerminalIndex !== -1) {
          setSelectTerminal(terminals[prevSelectedTerminalIndex])
          return
        }
      }
      setSelectTerminal(terminals[0])
    })
  }

  useEffect(() => {
    fetchSmaregiTerminal()
  }, [])

  return (
    <>
      {isShowSuccessModal ? (
        <PosCalculateSuccessModal onClose={() => props.onClose(true)} />
      ) : (
        <div css={modalWrapperStyle}>
          <div css={modalContentStyle}>
            <div css={modalTitleWrapperStyle}>
              <p css={modalTitleStyle}>{t('Send to Smaregi')}</p>
            </div>

            <div css={contentWrapperStyle}>
              <div css={contentTerminalAreaStyle}>
                <div style={{ fontSize: 12, fontWeight: 'bold', letterSpacing: 0.6, paddingRight: 16 }}>{t('Transmitting terminal')}</div>
                <select css={selectBoxStyle} onChange={e => changedTerminal(Number(e.target.value))}>
                  {terminalOptions.map(opt => (
                    <option key={opt.value} value={opt.value} selected={selectTerminal === opt.data}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <AdjustmentSalesSelectList
                {...props}
                onChange={(selectedSaleIds: string[], amount: number) => {
                  setSelectedSaleIds(selectedSaleIds)
                  setSumSelectSalePrice(amount)
                }}
              />
            </div>

            <div css={footerButtonWrapperStyle}>
              <Button width={110} height={38} marginRight={16} buttonType={3} onClick={() => props.onClose(false)}>
                {t('Cancel')}
              </Button>
              <Button width={110} height={38} buttonType={isDisabledSend ? 5 : 1} isDisabled={isDisabledSend} onClick={sendPosCalculate}>
                {t('Submit')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

const modalWrapperStyle = css({
  boxShadow: '0px 0px 6px #0000001A',
  border: '1px solid #F2F2F2',
  borderRadius: 5,
  marginRight: 16,
  marginBottom: 16,
  width: '100vw',
  height: '100vh',
  background: 'rgba(39, 39, 39, 0.5)',
  position: 'fixed',
  top: 0,
  left: 0,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  overflow: 'scroll',
  zIndex: 100,
})

const modalContentStyle = css({
  width: 900,
  maxHeight: 650,
  background: '#fff',
  borderRadius: 5,
  marginBottom: 40,
})

const modalTitleWrapperStyle = css({
  width: 'inherit',
  paddingLeft: 32,
  position: 'absolute',
  backgroundColor: 'white',
  borderRadius: '5px 5px 0px 0px',
})

const modalTitleStyle = css({
  color: '#272727',
  fontSize: 18,
  fontWeight: 'bold',
  lineHeight: '60px',
  letterSpacing: '0.9px',
})

const contentWrapperStyle = css({
  width: '100%',
  padding: '60px 32px 32px',
  background: '#f2f2f2',
  display: 'flex',
  flexFlow: 'column',
  borderRadius: '5px 5px 0 0',
  div: {
    color: '#676767',
  },
})

const contentTerminalAreaStyle = css({
  display: 'flex',
  alignItems: 'center',
  padding: '17px 0',
})

const footerButtonWrapperStyle = css({
  height: 60,
  paddingRight: 32,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  width: 'inherit',
  position: 'absolute',
  backgroundColor: 'white',
  borderRadius: '0px 0px 5px 5px',
})

const selectBoxStyle = css(initializeSelectBoxStyle, {
  height: 32,
  width: 278,
  border: '1px solid #CCCCCC',
  borderRadius: 16,
  backgroundImage: `url(${require('@/static/images/select.svg')})`,
  backgroundRepeat: 'no-repeat',
  backgroundSize: 10,
  display: 'block',
  paddingLeft: 14,
  paddingRight: 14,
  marginRight: 10,
  backgroundPosition: '90% center',
})
